import { useMemo, useState } from 'react';
import { Button, Dialog, Select, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useParam } from '../../pages/statistics/shared';
import { printQueue, type TokenRole } from '../api/client';
import { usePrintQueueLinks } from '../links';
import { TenantCell, tenantLabel, usePrintQueueTarget, usePrintQueueWrite } from '../shared';
import '../printqueue.css';

const ROLES: { value: TokenRole; label: string }[] = [
  { value: 'PrintServer', label: 'PrintServer — an on-site print server' },
  { value: 'WorkServer', label: 'WorkServer — queues jobs' },
];

/**
 * The print queue's own tokens (WorkServers queue with one, print servers poll with one). Listed masked: only the hub
 * holds them; a new one is shown once.
 */
export function PrintQueueTokensPage() {
  const target = usePrintQueueTarget();
  const api = useMemo(() => printQueue(target), [target]);
  const canWrite = usePrintQueueWrite();
  const links = usePrintQueueLinks(target);
  const [tenant, setTenant] = useParam('tenant', '');
  const tokens = useAsync(() => api.tokens(), [api]);
  const toast = useNotice();
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    tenantId: '',
    name: '',
    role: 'PrintServer' as TokenRole,
    days: '365',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const all = tokens.data ?? [];
  const tenants = [...new Set(all.map((t) => t.tenantId ?? '').filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );
  const rows = all.filter((t) => !tenant || t.tenantId === tenant);

  const open = () => {
    setDraft({ tenantId: tenant, name: '', role: 'PrintServer', days: '365' });
    setError(null);
    setCreated(null);
    setCreating(true);
  };
  const create = async () => {
    const days = Number(draft.days);
    if (!draft.tenantId.trim() || !draft.name.trim()) return setError('Tenant and name are required.');
    if (!Number.isInteger(days) || days < 1 || days > 3650) return setError('Valid for 1–3650 days.');
    setBusy(true);
    setError(null);
    try {
      const r = await api.createToken({
        tenantId: draft.tenantId.trim(),
        name: draft.name.trim(),
        role: draft.role,
        expireDays: days,
      });
      setCreated(r.token);
      await tokens.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (id: string, label: string) => {
    try {
      await api.deleteTokens([id]);
      toast.success(`Revoked ${label}`);
      await tokens.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Page
      title="Tokens"
      description="The print queue's own tokens: WorkServers queue jobs with one, print servers fetch them with one. Revoking stops that client at once."
      actions={
        canWrite && (
          <Button size="sm" onClick={open}>
            New token
          </Button>
        )
      }
    >
      {toast.element}
      <div className="pq-toolbar">
        <Select
          aria-label="Tenant"
          value={tenant}
          onChange={setTenant}
          options={[
            { value: '', label: `All tenants (${tenants.length})` },
            ...tenants.map((t) => ({ value: t, label: tenantLabel(t, links) })),
          ]}
        />
      </div>
      <ErrorBox error={tokens.error} />
      <div className="nyk-card">
        <Loading loading={tokens.loading && !tokens.data} />
        {!!rows.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="pq-tokens">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tenant</th>
                  <th>Role</th>
                  <th>Expires</th>
                  <th>Added</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const expired = !!t.expires && new Date(t.expires).getTime() < Date.now();
                  const label = `${t.name ?? 'token'} ${t.hint}`;
                  return (
                    <tr key={t.id} data-token={t.id}>
                      <td className="nyk-card-title">
                        <b>{t.name ?? '—'}</b>
                        <div className="nyk-ui-hint nyk-mono">{t.hint}</div>
                      </td>
                      <td data-label="Tenant">
                        {t.tenantId ? (
                          <TenantCell tenantId={t.tenantId} links={links} target={target} />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td data-label="Role">{t.role ?? '—'}</td>
                      <td data-label="Expires" className={expired ? 'nyk-text-red' : undefined}>
                        {t.expires ? `${formatDateTime(t.expires)} (${formatRelative(t.expires)})` : 'never'}
                      </td>
                      <td data-label="Added">{formatDateTime(t.dateAdded)}</td>
                      <td className="nyk-row-actions">
                        {canWrite && t.role !== 'Admin' && (
                          <ConfirmButton
                            size="sm"
                            variant="ghost"
                            message={`Revoke ${label}? The client using it stops working at once.`}
                            onConfirm={() => void revoke(t.id, label)}
                          >
                            Revoke
                          </ConfirmButton>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroller>
        )}
        {tokens.data && !rows.length && <EmptyState title="No tokens." />}
      </div>
      <Dialog
        open={creating}
        onClose={() => setCreating(false)}
        title={created ? 'Token created' : 'New print queue token'}
        footer={
          created ? (
            <Button onClick={() => setCreating(false)}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setCreating(false)}>
                Cancel
              </Button>
              <Button loading={busy} onClick={() => void create()}>
                Create
              </Button>
            </>
          )
        }
      >
        {created ? (
          <div className="pq-form">
            <p>
              Copy it now: it is not shown again. Configure it on the {draft.role} of {draft.tenantId}.
            </p>
            <div className="pq-token" data-testid="pq-new-token">
              {created}
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void navigator.clipboard?.writeText(created).then(() => toast.success('Copied'))}
            >
              Copy
            </Button>
          </div>
        ) : (
          <div className="pq-form">
            <ErrorBox error={error} />
            <TextField
              label="Tenant id"
              hint="The print queue TenantId the WorkServer uses."
              value={draft.tenantId}
              onChange={(e) => setDraft({ ...draft, tenantId: e.target.value })}
              list="pq-tenants"
            />
            <datalist id="pq-tenants">
              {tenants.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <TextField
              label="Name"
              hint="Which client gets it, e.g. the print server name."
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <Select
              label="Role"
              value={draft.role}
              onChange={(v) => setDraft({ ...draft, role: v as TokenRole })}
              options={ROLES}
            />
            <TextField
              label="Valid for (days)"
              type="number"
              inputMode="numeric"
              min={1}
              max={3650}
              value={draft.days}
              onChange={(e) => setDraft({ ...draft, days: e.target.value })}
            />
          </div>
        )}
      </Dialog>
    </Page>
  );
}
